import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import type { UserRow } from '../../types/User';
import { useRoles } from '../../hooks/useRoles';
import { t } from '../../i18n/t';

// Popup multi-select editor for roleIds. Use with `cellEditorPopup: true` so
// the panel can be larger than the cell.
//
// AgGrid v35 does NOT auto-close a custom popup editor on outside click — the
// component must detect the outside click and call stopEditing() itself. We
// wire a document mousedown listener for that, plus Enter/Escape on the root.
// Each checkbox toggle pushes the latest selection via onValueChange, so
// stopEditing() simply closes with the already-staged value.
export function RolesCellEditor({
  value,
  onValueChange,
  stopEditing,
}: CustomCellEditorProps<UserRow, number[]>) {
  const { roles, loading } = useRoles();
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(value ?? []),
  );
  const rootRef = useRef<HTMLDivElement>(null);

  // Close + commit on click outside the popup root. Without this the popup
  // never detaches when the user clicks elsewhere (AgGrid won't do it for a
  // custom editor). Also covers the case where focus has left the editor, so
  // the Enter/Escape handler below alone is insufficient.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target;
      if (
        rootRef.current &&
        target instanceof Node &&
        !rootRef.current.contains(target)
      ) {
        stopEditing();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [stopEditing]);

  const toggle = useCallback(
    (roleId: number) => {
      setSelected((prev) => {
        const next = new Set(Array.from(prev));
        if (next.has(roleId)) next.delete(roleId);
        else next.add(roleId);
        onValueChange(Array.from(next).sort((a, b) => a - b));
        return next;
      });
    },
    [onValueChange],
  );

  return (
    <div
      ref={rootRef}
      className="lop-roles-editor"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') stopEditing();
      }}
    >
      {loading || !roles ? (
        <div className="lop-roles-editor__status">{t('common.loading')}</div>
      ) : (
        roles
          .slice()
          .sort((a, b) => a.roleId - b.roleId)
          .map((r) => (
            <label key={r.roleId} className="lop-roles-editor__item">
              <input
                type="checkbox"
                checked={selected.has(r.roleId)}
                onChange={() => toggle(r.roleId)}
              />
              <span>{r.roleName}</span>
            </label>
          ))
      )}
    </div>
  );
}
